import { useEffect, useMemo, useState } from "react";
import { DEFAULT_REFUND_FORM } from "../adminConstants";
import {
  formatAmountInput,
  formatCurrency,
  mapAdminBookingDetail,
  mapAdminBookingSummary,
  parseAmount,
} from "../adminUtils";
import { getAdminBooking, refundAdminBooking } from "../../../api/adminBookings";

const useAdminRefunds = ({ bookings, setBookings, showFeedback, refreshAudit }) => {
  const [refundDrawerOpen, setRefundDrawerOpen] = useState(false);
  const [refundBookingId, setRefundBookingId] = useState(null);
  const [refundForm, setRefundForm] = useState(() => ({ ...DEFAULT_REFUND_FORM }));
  const [refundBooking, setRefundBooking] = useState(null);
  const [refundBookingLoading, setRefundBookingLoading] = useState(false);
  const [refundBookingError, setRefundBookingError] = useState("");
  const [isRefunding, setIsRefunding] = useState(false);

  useEffect(() => {
    if (!refundDrawerOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setRefundDrawerOpen(false);
        setRefundBookingId(null);
        setRefundForm({ ...DEFAULT_REFUND_FORM });
        setRefundBooking(null);
        setRefundBookingError("");
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [refundDrawerOpen]);

  useEffect(() => {
    if (!refundBookingId) {
      setRefundBooking(null);
      setRefundBookingError("");
      setRefundBookingLoading(false);
      return undefined;
    }

    let cancelled = false;
    setRefundBookingLoading(true);
    setRefundBookingError("");

    const loadBooking = async () => {
      try {
        const payload = await getAdminBooking(refundBookingId);
        if (!cancelled) {
          setRefundBooking(mapAdminBookingDetail(payload));
        }
      } catch (error) {
        if (!cancelled) {
          setRefundBookingError(error?.message || "Unable to load booking.");
        }
      } finally {
        if (!cancelled) setRefundBookingLoading(false);
      }
    };

    loadBooking();

    return () => {
      cancelled = true;
    };
  }, [refundBookingId]);

  const selectedBooking = useMemo(() => {
    if (refundBooking) return refundBooking;
    return bookings.find((booking) => booking.id === refundBookingId) || null;
  }, [bookings, refundBooking, refundBookingId]);

  const refundableAmount = selectedBooking
    ? Math.max(
        0,
        (selectedBooking.totalPaid ?? 0) - (selectedBooking.refundedAmount ?? 0)
      )
    : 0;

  const refundAmountValue = selectedBooking
    ? refundForm.isFull
      ? refundableAmount
      : refundForm.isHalf
        ? refundableAmount * 0.5
      : parseAmount(refundForm.amount)
    : 0;

  const refundFeeEstimate =
    selectedBooking && refundAmountValue > 0
      ? Math.max(0, refundAmountValue * 0.029 + 0.3)
      : 0;

  const refundNet = Math.max(0, refundAmountValue - refundFeeEstimate);
  const refundRequiresNote = ["goodwill", "other"].includes(refundForm.reason);
  const refundHistory = selectedBooking?.refunds ?? [];
  const selectedCurrency = selectedBooking?.currency;

  const openRefundDrawer = (booking) => {
    if (!booking) return;
    const refundable = Math.max(0, (booking.totalPaid ?? 0) - (booking.refundedAmount ?? 0));
    setRefundBookingId(booking.id);
    setRefundBooking(booking);
    setRefundBookingError("");
    setRefundForm({
      isFull: true,
      isHalf: false,
      amount: formatAmountInput(refundable),
      reason: "cancellation",
      note: "",
    });
    setRefundDrawerOpen(true);
  };

  const closeRefundDrawer = () => {
    setRefundDrawerOpen(false);
    setRefundBookingId(null);
    setRefundForm({ ...DEFAULT_REFUND_FORM });
    setRefundBooking(null);
    setRefundBookingError("");
  };

  const handleRefundSubmit = async (event) => {
    event.preventDefault();
    if (!selectedBooking) return;
    if (refundAmountValue <= 0) {
      showFeedback("Refund amount must be greater than 0.", "warning");
      return;
    }
    if (refundAmountValue > refundableAmount + 0.01) {
      showFeedback("Refund exceeds remaining paid amount.", "warning");
      return;
    }
    if (refundRequiresNote && !refundForm.note.trim()) {
      showFeedback("Add a note for goodwill or other refunds.", "warning");
      return;
    }
    setIsRefunding(true);
    try {
      const updated = await refundAdminBooking(selectedBooking.id, {
        amount: refundAmountValue,
        reason: refundForm.reason,
        notes: refundForm.note || null,
      });
      const mapped = mapAdminBookingSummary(updated);
      setBookings((prev) =>
        prev.map((booking) => (booking.id === selectedBooking.id ? mapped : booking))
      );
      setRefundBooking(mapAdminBookingDetail(updated));
      await refreshAudit({ silent: true });
      showFeedback(
        `Refund of ${formatCurrency(refundAmountValue, selectedBooking.currency)} issued for ${
          selectedBooking.id
        }.`,
        "success"
      );
      closeRefundDrawer();
    } catch (error) {
      showFeedback(error?.message || "Refund failed. Please try again.", "warning");
    } finally {
      setIsRefunding(false);
    }
  };

  return {
    refundDrawerOpen,
    refundBookingId,
    refundForm,
    setRefundForm,
    refundBooking,
    refundBookingLoading,
    refundBookingError,
    isRefunding,
    openRefundDrawer,
    closeRefundDrawer,
    handleRefundSubmit,
    selectedBooking,
    refundableAmount,
    refundAmountValue,
    refundFeeEstimate,
    refundNet,
    refundRequiresNote,
    refundHistory,
    selectedCurrency,
  };
};

export default useAdminRefunds;
